/**
 * **One client's conversation in the side panel** — what a host needs to open `ChatDock` over
 * itself: the conversation found in the inbox or opened for the client, the plan line under the
 * name, and where a clip in the thread leads.
 *
 * Promoted out of `pages/tasks` on its second host, the Pulpit's „Napisz" (2 November 2026): the
 * two screens open the same panel the same way, and two copies of the open would drift the way
 * the "found in the inbox, else opened by e-mail" order once nearly did.
 *
 * The host supplies what it already knows about its clients (`directory`): the e-mail the open
 * would otherwise read again, and the plan name. Opening another client swaps the conversation
 * without closing the panel.
 */

import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { conversationOfCustomer } from '../model/conversationRows';
import type { OpenFailure } from '../model/openFailure';
import type { ChatDockProps } from '../ui/ChatDock';

import { useConversations } from './useChatReads';
import { useOpenConversation } from './useOpenConversation';

/** What the host knows about one client, or `null` where it knows nothing. */
export interface ChatDirectoryEntry {
  readonly email: string | null;
  readonly planName: string | null;
}

export interface ClientChat {
  /** The client whose conversation is open, or `null` with the panel closed. */
  readonly talkingTo: number | null;
  readonly open: (customerId: number) => void;
  /** The panel's props, or `null` while it is closed. */
  readonly dock: ChatDockProps | null;
}

interface OpenChat {
  readonly customerId: number;
  readonly conversationId: number | null;
  readonly unread: number;
  readonly failure: OpenFailure | null;
}

export function useClientChat(directory: (customerId: number) => ChatDirectoryEntry | null): ClientChat {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inbox = useConversations();
  const opener = useOpenConversation();
  const [chat, setChat] = useState<OpenChat | null>(null);

  const open = (customerId: number) => {
    const known = conversationOfCustomer(inbox.rows, customerId);
    setChat({ customerId, conversationId: known?.id ?? null, unread: known?.unread ?? 0, failure: null });
    if (known !== null) return;
    void opener.open(customerId, directory(customerId)?.email ?? null).then(({ id, failure }) => {
      setChat((current) =>
        current?.customerId === customerId ? { ...current, conversationId: id, failure } : current,
      );
    });
  };

  return {
    talkingTo: chat?.customerId ?? null,
    open,
    dock:
      chat === null
        ? null
        : {
            conversationId: chat.conversationId,
            subline: directory(chat.customerId)?.planName ?? t('chat.window.noPlan'),
            unreadAtOpen: chat.unread,
            opening: opener.opening === chat.customerId,
            failure: chat.failure,
            onClose: () => {
              setChat(null);
            },
            onOpenClip: (customerId, clipId) => {
              void navigate({
                to: '/klienci/$clientId',
                params: { clientId: String(customerId) },
                search: { tab: 'forma', clip: clipId },
              });
            },
          },
  };
}
